import { FC, useEffect } from 'react'
import { useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { Transition, TransitionStatus } from 'react-transition-group'

import { IconButton, Tooltip } from '@material-ui/core'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMousePointer, faChevronCircleLeft, faChevronCircleRight, faCubes } from '@fortawesome/free-solid-svg-icons'

import { sidebarAnimationDuration, leftSidebarWidth as sidebarWidth, tooltipDelay } from 'src/Constants'
import { generalActions } from 'src/actions/GeneralActions'
import { profileActions } from 'src/actions/ProfileActions'
import { leftSideBarStyle } from './LeftSideBar.style'
import LayerPicker from '../layerpicker/LayerPicker'
import { RootState } from 'src/Store'
import { ToolType } from 'src/Enums'
import { infoMenuActions } from 'src/actions/InfoMenuActions'
import { GeoModel } from 'src/dataSources/geo/GeoModel'
import { boreholeInfoActions } from 'src/actions/BoreholeInfoActions'
import { virtualBoreholeInfoActions } from 'src/actions/VirtualBoreholeInfoActions'
import { wikiActions } from 'src/actions/WikiActions'
import { InfoView } from 'src/reducers/InfoMenuReducer'

import { LeftSideBarIcon, LeftSideBarIconType } from './leftsidebaricon/LeftSideBarIcon'
import { getBarStyle, getBarTransitionStyle } from 'src/utils/Utils'

export interface LeftSideBarProps {
  open: boolean
}

const defaultProps: LeftSideBarProps = {
  open: true
}

/**
 * LeftSideBar React component.
 * @param {object} props React properties object.
 * The following properties are implemented:
 * open {boolean}: If true the component will be open initially.
 * @returns React view component.
 */
export const LeftSideBar: FC<LeftSideBarProps> = (props: LeftSideBarProps) => {
  const classes = leftSideBarStyle()
  const dispatch = useDispatch()
  const intl = useIntl()

  const showLeftSideBar = useSelector((state: RootState): boolean => state.generalState.showLeftSideBar)
  const selectedTool = useSelector((state: RootState): ToolType => state.generalState.selectedTool)
  const selectedGeoModel = useSelector((state: RootState): GeoModel | undefined => state.generalState.selectedGeoModel)
  const username = useSelector((state: RootState): string => state.authState.username)

  // Styles for sidebar in different transitions
  const sidebarStyle = getBarStyle(sidebarAnimationDuration)
  const sidebarTransitionStyles = getBarTransitionStyle(sidebarWidth)

  /**
   * If not geo model is selected default to info tool.
   */
  useEffect(() => {
    if (selectedGeoModel === undefined) {
      dispatch(generalActions.setSelectedTool(ToolType.INFO))
    }
    // eslint-disable-next-line
  }, [selectedGeoModel])

  /**
   * When toggle button is clicked the sidebar is toggled.
   */
  const toggleSideBarClicked = (): void => {
    dispatch(generalActions.setShowLeftSideBar(!showLeftSideBar))
  }

  /**
   * Handle when the info tool button is clicked.
   */
  const infoToolClicked = (): void => {
    dispatch(boreholeInfoActions.clearState())
    dispatch(profileActions.clearState())
    dispatch(virtualBoreholeInfoActions.clearState())
    dispatch(infoMenuActions.currentView(InfoView.Empty))
    dispatch(generalActions.setSelectedTool(ToolType.INFO))
    dispatch(generalActions.setSelectedGraph(undefined))
  }

  /**
   * Handle when the profile tool button is clicked.
   */
  const profileToolClicked = (): void => {
    if (selectedTool === ToolType.PROFILE) {
      return
    }
    dispatch(boreholeInfoActions.clearState())
    dispatch(profileActions.clearState())
    dispatch(virtualBoreholeInfoActions.clearState())
    dispatch(generalActions.setSelectedGraph(undefined))
    dispatch(infoMenuActions.currentView(InfoView.Empty))
    dispatch(wikiActions.clearViews())
    dispatch(generalActions.setSelectedTool(ToolType.PROFILE))
  }

  /**
   * Handle when the virtual borehole tool button is clicked.
   */
  const virtualBoreholeToolClicked = (): void => {
    if (selectedGeoModel === undefined) {
      return
    }
    if (selectedTool === ToolType.VIRTUAL_BOREHOLE) {
      return
    }

    dispatch(boreholeInfoActions.clearState())
    dispatch(profileActions.clearState())
    dispatch(virtualBoreholeInfoActions.clearState())
    dispatch(generalActions.setSelectedGraph(undefined))
    dispatch(infoMenuActions.currentView(InfoView.Empty))
    dispatch(wikiActions.clearViews())
    dispatch(generalActions.setSelectedTool(ToolType.VIRTUAL_BOREHOLE))
  }

  /**
   * Handler for the 3D view tool button.
   */
  const _3dViewClicked = (): void => {
    // Updated the selected tool.
    const selectTool = selectedTool !== ToolType._3D_VIEW
    if (selectTool) {
      dispatch(generalActions.setSelectedTool(ToolType._3D_VIEW))

      // Close profile tool.
      dispatch(profileActions.clearState())
      // Close info view on right hand side.
      dispatch(infoMenuActions.currentView(InfoView.Empty))
      dispatch(wikiActions.clearViews())
    }
  }

  return (
    <>
      <Transition in={showLeftSideBar} timeout={0}>
        {(state: TransitionStatus) => (
          <div
            className={classes.root}
            style={{ ...sidebarStyle, ...sidebarTransitionStyles[state] }}
          >
            <LayerPicker visible={showLeftSideBar} />
            <div className={classes.tools}>
              <Tooltip
                title={intl.formatMessage({ id: 'tools.' + (showLeftSideBar ? 'hide' : 'show') + '.sidebar' })}
                enterDelay={tooltipDelay}
                placement='right'
              >
                <IconButton
                  size='medium'
                  className={classes.tool}
                  onClick={toggleSideBarClicked}
                >
                  {showLeftSideBar && <FontAwesomeIcon icon={faChevronCircleLeft} />}
                  {!showLeftSideBar && <FontAwesomeIcon icon={faChevronCircleRight} />}
                </IconButton>
              </Tooltip>
              <Tooltip
                title={intl.formatMessage({ id: 'tools.info' })}
                enterDelay={tooltipDelay}
                placement='right'
              >
                <IconButton
                  size='medium'
                  onClick={infoToolClicked}
                  className={selectedTool === ToolType.INFO ? classes.toolSelected : classes.tool}
                >
                  <FontAwesomeIcon icon={faMousePointer} />
                </IconButton>
              </Tooltip>
              <Tooltip
                title={intl.formatMessage({ id: 'tools.profile' })}
                enterDelay={tooltipDelay}
                placement='right'
              >
                <IconButton
                  size='medium'
                  onClick={profileToolClicked}
                >
                  <LeftSideBarIcon icon={LeftSideBarIconType.Profile} selected={selectedTool === ToolType.PROFILE} disabled={false} />
                </IconButton>
              </Tooltip>
              <Tooltip
                title={intl.formatMessage({ id: 'tools.virtualborehole' })}
                enterDelay={tooltipDelay}
                placement='right'
              >
                <IconButton
                  size='medium'
                  onClick={virtualBoreholeToolClicked}
                >
                  <LeftSideBarIcon icon={LeftSideBarIconType.VirtualBorehole} selected={selectedTool === ToolType.VIRTUAL_BOREHOLE} disabled={selectedGeoModel === undefined} />
                </IconButton>
              </Tooltip>
              {username === 'geoatlas@alexandra.dk' && (
                <Tooltip
                  title={intl.formatMessage({ id: 'tools._3dview' })}
                  enterDelay={tooltipDelay}
                  placement='right'
                >
                  <IconButton
                    size='medium'
                    onClick={_3dViewClicked}
                    className={selectedTool === ToolType._3D_VIEW ? classes.toolSelected : classes.tool}
                  >
                    <FontAwesomeIcon icon={faCubes} />
                  </IconButton>
                </Tooltip>
              )}
            </div>
          </div>
        )}
      </Transition>
    </>
  )
}

LeftSideBar.defaultProps = defaultProps

export default LeftSideBar
